import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { utcTimeStringToDisplay24Hour } from '../../../util/bookingTime';
import { calculateCampDates, formatDateToKey } from '../../ProviderPage/utils/campUtils';

/**
 * Scheduled calendar day (coach-selected) + UTC-stored HH:mm → instant in listing timezone.
 */
export const momentFromScheduledDateAndUtcTime = (dateString, utcTimeStr, listingTimeZone) => {
  if (!dateString || !utcTimeStr || !listingTimeZone) return null;
  const localHHmm = utcTimeStringToDisplay24Hour(utcTimeStr, listingTimeZone);
  if (!localHHmm) return null;
  const m = moment.tz(`${dateString} ${localHHmm}`, 'YYYY-MM-DD HH:mm', listingTimeZone);
  return m.isValid() ? m : null;
};

/** Coach-scheduled day + UTC slot → customer's local calendar day (YYYY-MM-DD). */
export const customerDateFromScheduled = (
  coachDate,
  utcTimeStr,
  listingTimeZone,
  customerTimeZone
) => {
  if (!coachDate) return '';
  if (!listingTimeZone || !customerTimeZone || listingTimeZone === customerTimeZone) {
    return coachDate;
  }
  const instant = momentFromScheduledDateAndUtcTime(coachDate, utcTimeStr, listingTimeZone);
  return instant ? instant.clone().tz(customerTimeZone).format('YYYY-MM-DD') : coachDate;
};

/**
 * Normalize time string to a consistent format for parsing
 * Handles formats like "12:00 PM", "12:00pm", "12:00 PM", etc.
 * @param {string} timeStr - Time string in various formats
 * @returns {string} Normalized time string or empty string
 */
const normalizeTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return '';
  // Trim and ensure consistent spacing
  return timeStr.trim().replace(/\s+/g, ' ');
};

/**
 * Parse time string to moment object
 * @param {string} timeStr - Time string (e.g., "12:00 PM", "12:00pm")
 * @returns {moment.Moment|null} Parsed moment object or null
 */
export const parseTime = (timeStr) => {
  let normalized = normalizeTimeString(timeStr);
  if (!normalized) return null;

  // If it's a range (e.g., "7:00 AM - 9:00 AM"), extract the first part
  if (normalized.includes('-')) {
    normalized = normalized.split('-')[0].trim();
  }

  // Try parsing with various formats
  const formats = ['h:mm A', 'h:mmA', 'HH:mm', 'H:mm'];

  for (const format of formats) {
    const parsed = moment(normalized, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

/**
 * Format moment time to "h:mm A" format (e.g., "12:00 PM")
 * @param {moment.Moment} time - Moment time object
 * @returns {string} Formatted time string
 */
export const formatTimeSlot = (time) => {
  if (!time || !time.isValid()) return '';
  return time.format('h:mm A');
};

/**
 * Format a time range string given a start time and duration or end time
 * @param {string} startTimeStr - Start time (e.g., "7:00 AM")
 * @param {number|string} durationOrEndTime - Duration in minutes or end time string
 * @param {string} dateString - Optional date string for context
 * @param {string} listingTimeZone - Optional listing timezone
 * @param {string} customerTimeZone - Optional customer timezone
 * @returns {string} Formatted range (e.g., "7:00 AM - 8:00 AM")
 */
export const formatTimeRange = (startTimeStr, durationOrEndTime, block = null, dateString = null, listingTimeZone = null, customerTimeZone = null) => {
  let start = parseTime(startTimeStr);
  if (!start) return startTimeStr;

  if (dateString && customerTimeZone) {
    const startMoment = moment.tz(
      `${dateString} ${start.format('HH:mm')}`,
      'YYYY-MM-DD HH:mm',
      listingTimeZone || 'Etc/UTC'
    );
    start = startMoment.clone().tz(customerTimeZone);
  }

  let end;
  if (typeof durationOrEndTime === 'number') {
    end = start.clone().add(durationOrEndTime, 'minutes');
  } else {
    const parsedEnd = parseTime(durationOrEndTime);
    if (parsedEnd) {
      if (dateString && customerTimeZone) {
        const endMoment = moment.tz(
          `${dateString} ${parsedEnd.format('HH:mm')}`,
          'YYYY-MM-DD HH:mm',
          listingTimeZone || 'Etc/UTC'
        );
        end = endMoment.clone().tz(customerTimeZone);
      } else {
        end = parsedEnd;
      }
    }
  }

  if (!end) return startTimeStr;
  return block?.type === 'class' ? `${formatTimeSlot(start)} - ${formatTimeSlot(end)}` : `${formatTimeSlot(start)}`;
};

/**
 * Generate time slots for camp session (single time slot from start time)
 * @param {Object} camp - Camp object with dailySchedule.startTime
 * @returns {string[]} Array of time slot strings (single element)
 */
export const getCampTimeSlots = (camp) => {
  if (!camp || !camp.dailySchedule || !camp.dailySchedule.startTime) {
    return [];
  }

  const startTime = parseTime(camp.dailySchedule.startTime);
  if (!startTime) {
    console.warn('Could not parse camp start time:', camp.dailySchedule.startTime);
    return [];
  }

  return [formatTimeSlot(startTime)];
};

/**
 * Generate time slots for session/class based on start time, end time, and duration
 * @param {string} startTime - Start time string (e.g., "12:00 PM")
 * @param {string} endTime - End time string (e.g., "3:00 PM")
 * @param {number} durationMinutes - Duration in minutes (e.g., 30)
 * @returns {string[]} Array of time slot strings
 */
export const getSessionTimeSlots = (startTime, endTime, durationMinutes) => {
  if (!startTime || !endTime || !durationMinutes || durationMinutes <= 0) {
    return [];
  }

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  if (!start || !end) {
    console.warn('Could not parse time:', { startTime, endTime });
    return [];
  }

  // Ensure end time is after start time (handle day rollover if needed)
  if (end.isBefore(start)) {
    end.add(1, 'day');
  }

  const slots = [];
  const current = start.clone();

  // Generate slots every `durationMinutes` minutes
  // Include a slot if adding the duration to it doesn't exceed the end time
  while (current.isBefore(end) || current.isSame(end)) {
    // Only add slot if the session (current + duration) can fit before or at end time
    const slotEnd = current.clone().add(durationMinutes, 'minutes');
    if (slotEnd.isAfter(end)) {
      break;
    }

    slots.push(formatTimeSlot(current));
    current.add(durationMinutes, 'minutes');

    // Safety check to prevent infinite loops
    if (slots.length > 100) {
      console.warn('Too many time slots generated, stopping');
      break;
    }
  }

  return slots;
};

/**
 * Get listing dates that could potentially land on a customer date
 * @param {string} customerDate - Date in 'YYYY-MM-DD' format
 * @param {string} listingTimeZone - Listing's timezone
 * @param {string} customerTimeZone - Customer's timezone
 * @returns {string[]} Array of date strings
 */
export const getListingDatesForCustomerDate = (customerDate, listingTimeZone, customerTimeZone) => {
  if (!listingTimeZone || !customerTimeZone) return [customerDate];
  const m = moment.tz(customerDate, 'YYYY-MM-DD', customerTimeZone);
  return [
    m.clone().subtract(1, 'day').format('YYYY-MM-DD'),
    m.format('YYYY-MM-DD'),
    m.clone().add(1, 'day').format('YYYY-MM-DD')
  ];
};

/**
 * @param {Object} sessionType - Session type object
 * @param {Object} availabilityData - Contains camps, schedulingBlocks
 * @param {string} customerDate - The customer's selected date in 'YYYY-MM-DD'
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's timezone
 * @returns {string[]} Array of time slot strings (converted to customer's timezone)
 */
export const getTimeSlotsForSessionType = (sessionType, availabilityData, customerDate = null, listingTimeZone = null, customerTimeZone = null) => {
  if (!sessionType) return [];

  // Helper to get raw slots for a listing date
  const getRawSlots = () => {
    if (sessionType.isCampSession && sessionType.campId) {
      const camp = availabilityData.camps[sessionType.campId];
      return camp ? getCampTimeSlots(camp) : [];
    } else if (sessionType.availabilityId) {
      const block = availabilityData.schedulingBlocks[sessionType.availabilityId];
      if (block) {
        if (block.type === 'class' && block.timeSlots?.length > 0) {
          return block.timeSlots.map(slot => {
            const start = parseTime(slot.startTime);
            const end = parseTime(slot.endTime);
            return start && end ? `${formatTimeSlot(start)} - ${formatTimeSlot(end)}` : (start ? formatTimeSlot(start) : null);
          }).filter(Boolean);
        } else if (block.startTime && block.endTime && block.duration) {
          return getSessionTimeSlots(block.startTime, block.endTime, block.duration);
        }
      }
    }
    return [];
  };

  const rawSlots = getRawSlots();

  // If no timezone context or no date selected, return raw slots formatted
  if (!customerDate || !listingTimeZone || !customerTimeZone) {
    return rawSlots;
  }

  const block =
    sessionType.availabilityId && availabilityData.schedulingBlocks[sessionType.availabilityId];
  const scheduledForBlock = (availabilityData.scheduledDates || []).filter(
    sd => sd.blockId === sessionType.availabilityId
  );

  const resultSlots = [];

  const pushSlot = (startM, endM) => {
    if (!startM) return;
    const startConverted = startM.clone().tz(customerTimeZone);
    if (startConverted.format('YYYY-MM-DD') !== customerDate) return;
    if (endM) {
      const endConverted = endM.clone().tz(customerTimeZone);
      resultSlots.push(`${formatTimeSlot(startConverted)} - ${formatTimeSlot(endConverted)}`);
    } else {
      resultSlots.push(formatTimeSlot(startConverted));
    }
  };

  if (sessionType.isCampSession && sessionType.campId) {
    const camp = availabilityData.camps[sessionType.campId];
    if (camp) {
      calculateCampDates(camp).forEach(coachDate => {
        const startM = momentFromScheduledDateAndUtcTime(
          coachDate,
          camp?.dailySchedule?.startTime,
          listingTimeZone
        );
        const endM = momentFromScheduledDateAndUtcTime(
          coachDate,
          camp?.dailySchedule?.endTime,
          listingTimeZone
        );
        pushSlot(startM, endM);
      });
    }
  } else if (block) {
    scheduledForBlock.forEach(sd => {
      const coachDate = sd.date;
      if (block.type === 'class' && block.timeSlots?.length > 0) {
        block.timeSlots.forEach(slot => {
          const startM = momentFromScheduledDateAndUtcTime(
            coachDate,
            slot.startTime,
            listingTimeZone
          );
          const endM = momentFromScheduledDateAndUtcTime(
            coachDate,
            slot.endTime,
            listingTimeZone
          );
          pushSlot(startM, endM);
        });
      } else if (block.startTime && block.endTime && block.duration) {
        const rangeStart = momentFromScheduledDateAndUtcTime(
          coachDate,
          block.startTime,
          listingTimeZone
        );
        const rangeEnd = momentFromScheduledDateAndUtcTime(
          coachDate,
          block.endTime,
          listingTimeZone
        );
        if (rangeStart && rangeEnd) {
          let current = rangeStart.clone();
          while (current.isBefore(rangeEnd)) {
            const slotEnd = current.clone().add(block.duration, 'minutes');
            if (slotEnd.isAfter(rangeEnd)) break;
            pushSlot(current, slotEnd);
            current.add(block.duration, 'minutes');
            if (resultSlots.length > 100) break;
          }
        }
      }
    });
  }

  return resultSlots;
};

/**
 * @param {Object} sessionType - The session type object
 * @param {Object} availabilityData - Contains camps, schedulingBlocks, scheduledDates
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's timezone
 * @returns {Set} Set of date strings in 'YYYY-MM-DD' format (customer's local calendar days)
 */
export const getAvailableDatesForSessionType = (sessionType, availabilityData, listingTimeZone = null, customerTimeZone = null) => {
  const availableDates = new Set();

  if (!sessionType) return availableDates;

  const addCustomerDate = (coachDate, utcTimeStr) => {
    const customerDay = customerDateFromScheduled(
      coachDate,
      utcTimeStr || '00:00',
      listingTimeZone,
      customerTimeZone
    );
    if (customerDay) availableDates.add(customerDay);
  };

  // For camp sessions
  if (sessionType.isCampSession && sessionType.campId) {
    const camp = availabilityData.camps[sessionType.campId];
    if (camp) {
      const startTime = camp.dailySchedule?.startTime || '00:00';
      calculateCampDates(camp).forEach(coachDate => addCustomerDate(coachDate, startTime));
    }
  }
  // For non-camp sessions
  else if (sessionType.availabilityId) {
    const block = availabilityData.schedulingBlocks[sessionType.availabilityId];

    if (block) {
      const matchingDates = availabilityData.scheduledDates.filter(
        sd => sd.blockId === sessionType.availabilityId
      );

      matchingDates.forEach(sd => {
        if (block.type === 'class' && block.timeSlots?.length > 0) {
          block.timeSlots.forEach(slot => addCustomerDate(sd.date, slot.startTime));
        } else {
          addCustomerDate(sd.date, block.startTime || '00:00');
        }
      });
    }
  }

  return availableDates;
};

/**
 * @param {Array} sessionTypes - Array of session types
 * @param {Object} availabilityData - Contains camps, schedulingBlocks, scheduledDates
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's timezone
 * @returns {Set} Set of all available dates in customer's timezone
 */
/**
 * Session types that are active and have at least one bookable date today or later.
 * Shared by listing detail "Choose Your Session" and coach profile Listings.
 */
export const filterSessionTypesWithFutureAvailability = (
  sessionTypes,
  availabilityData,
  listingTimeZone = null,
  customerTimeZone = null
) => {
  const today = moment()
    .tz(customerTimeZone || 'Etc/UTC')
    .startOf('day');

  return sessionTypes.filter((st) => {
    if (!st.isActive) return false;

    const hasAvailability = st.isCampSession ? st.campId : st.availabilityId;
    if (!hasAvailability) return false;

    const dates = getAvailableDatesForSessionType(
      st,
      availabilityData,
      listingTimeZone,
      customerTimeZone
    );

    if (!dates || dates.size === 0) return false;

    return Array.from(dates).some((dateStr) =>
      moment.tz(dateStr, 'YYYY-MM-DD', customerTimeZone || 'Etc/UTC').isSameOrAfter(today)
    );
  });
};

export const getAllAvailableDates = (sessionTypes, availabilityData, listingTimeZone = null, customerTimeZone = null) => {
  const allDates = new Set();

  sessionTypes.forEach(sessionType => {
    const dates = getAvailableDatesForSessionType(sessionType, availabilityData, listingTimeZone, customerTimeZone);
    dates.forEach(date => allDates.add(date));
  });

  return allDates;
};

/**
 * @param {string} dateString - Date in 'YYYY-MM-DD' format (customer's local date)
 * @param {Array} sessionTypes - Array of session types
 * @param {Object} availabilityData - Contains camps, schedulingBlocks, scheduledDates
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's timezone
 * @returns {Array} Array of session types available on this date, sorted by sortOrder
 */
export const getSessionTypesForDate = (dateString, sessionTypes, availabilityData, listingTimeZone = null, customerTimeZone = null) => {
  if (!dateString || !sessionTypes.length) return [];

  const available = sessionTypes.filter(sessionType => {
    const availableDates = getAvailableDatesForSessionType(sessionType, availabilityData, listingTimeZone, customerTimeZone);
    return availableDates.has(dateString);
  });

  // Sort by sortOrder (ascending), then by createdAt (descending) as fallback
  return available.sort((a, b) => {
    const sortOrderA = a.sortOrder ?? 0;
    const sortOrderB = b.sortOrder ?? 0;

    if (sortOrderA !== sortOrderB) {
      return sortOrderA - sortOrderB;
    }

    // If sortOrder is the same, sort by createdAt (newer first)
    const createdAtA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdAtB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return createdAtB - createdAtA;
  });
};

/**
 * @param {string} dateString - Date in 'YYYY-MM-DD' format (customer's local date)
 * @param {Array} sessionTypes - Array of session types
 * @param {Object} availabilityData - Contains camps, schedulingBlocks, scheduledDates
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's timezone
 * @returns {boolean} True if date has at least one available session type
 */
export const isDateAvailable = (dateString, sessionTypes, availabilityData, listingTimeZone = null, customerTimeZone = null) => {
  const availableSessionTypes = getSessionTypesForDate(dateString, sessionTypes, availabilityData, listingTimeZone, customerTimeZone);
  return availableSessionTypes.length > 0;
};

/**
 * Format Date object to 'YYYY-MM-DD' string (browser-local calendar components).
 * Prefer {@link formatDateKeyInTimezone} for customer-facing booking calendars.
 */
export const formatDateToString = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Calendar day key for year/month/day interpreted in an IANA timezone (not server local). */
export const formatDateKeyInTimezone = (year, monthIndex, day, timeZone) => {
  if (year == null || monthIndex == null || day == null) return '';
  if (!timeZone) {
    return formatDateToString(new Date(year, monthIndex, day));
  }
  const month = String(monthIndex + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return moment.tz(`${year}-${month}-${dayStr}`, 'YYYY-MM-DD', timeZone).format('YYYY-MM-DD');
};

/**
 * Convert date string and time slot to booking start timestamp
 * @param {string} dateString - Date in 'YYYY-MM-DD' format
 * @param {string} timeSlot - Time string (e.g., "12:00 PM")
 * @param {string} timeZone - Timezone string (e.g., "America/New_York")
 * @returns {Date|null} Booking start date or null
 */
export const convertTimeSlotToStartDate = (dateString, timeSlot, timeZone = 'UTC') => {
  if (!dateString || !timeSlot) return null;

  const parsedTime = parseTime(timeSlot);
  if (!parsedTime) return null;

  // Combine date and time in the listing's timezone
  const startDateTime = moment
    .tz(
      `${dateString} ${parsedTime.format('HH:mm')}`,
      'YYYY-MM-DD HH:mm',
      timeZone
    )
    .toDate();

  return startDateTime;
};

/**
 * Calculate booking end date from start date and duration
 * @param {Date} startDate - Booking start date
 * @param {number} durationMinutes - Duration in minutes
 * @returns {Date} Booking end date
 */
export const calculateBookingEndDate = (startDate, durationMinutes) => {
  if (!startDate || !durationMinutes || durationMinutes <= 0) {
    return startDate;
  }
  return new Date(startDate.getTime() + durationMinutes * 60000);
};

/**
 * @param {string} dateString - Date in 'YYYY-MM-DD' format (customer's local date)
 * @param {string} timeSlot - Time slot string (e.g., "12:00 PM" or "12:00 PM - 1:00 PM")
 * @param {Object} sessionType - Session type object
 * @param {Object} availabilityData - Contains camps, schedulingBlocks
 * @param {string} listingTimeZone - The listing's timezone
 * @param {string} customerTimeZone - The customer's local timezone
 * @returns {Object|null} Object with bookingStart and bookingEnd dates, or null
 */
export const getBookingTimestamps = (dateString, timeSlot, sessionType, availabilityData, listingTimeZone = 'UTC', customerTimeZone = null) => {
  if (!dateString || !timeSlot || !sessionType) return null;

  const tzForParsing = customerTimeZone || listingTimeZone;
  // If timezones match, we can parse directly as listing time to avoid any ambiguity
  const parseTZ = (customerTimeZone && listingTimeZone && customerTimeZone === listingTimeZone)
    ? listingTimeZone
    : tzForParsing;

  let startDate = convertTimeSlotToStartDate(dateString, timeSlot, parseTZ);
  if (!startDate) return null;

  let endDate;

  // Handle range if present in timeSlot
  if (timeSlot.includes(' - ')) {
    const parts = timeSlot.split(' - ');
    const endTimeStr = parts[1];
    const parsedEndTime = parseTime(endTimeStr);
    if (parsedEndTime) {
      endDate = moment.tz(`${dateString} ${parsedEndTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm', tzForParsing).toDate();
      // Handle day rollover if end time is before start time (e.g. 11 PM - 1 AM)
      if (endDate < startDate) {
        endDate = moment(endDate).add(1, 'day').toDate();
      }
    }
  }

  if (endDate) {
    return {
      bookingStart: startDate,
      bookingEnd: endDate,
    };
  }

  // For camp sessions, use camp's end time if not already determined
  if (sessionType.isCampSession && sessionType.campId) {
    const camp = availabilityData.camps[sessionType.campId];
    if (camp && camp.dailySchedule && camp.dailySchedule.endTime) {
      const endTime = parseTime(camp.dailySchedule.endTime);

      if (sessionType.pricingType === 'total') {
        const campDates = calculateCampDates(camp);
        if (campDates.length > 0) {
          const firstDate = campDates[0];
          const lastDate = campDates[campDates.length - 1];

          // For total pricing, we always use the original listing dates/times for the final range
          const start = convertTimeSlotToStartDate(firstDate, camp.dailySchedule.startTime, listingTimeZone);
          if (start) {
            startDate = start;
          }

          if (endTime) {
            endDate = moment
              .tz(`${lastDate} ${endTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm', listingTimeZone)
              .toDate();
          }
        }
      } else {
        // For individual camp sessions, we need to map back to the original listing date
        // But if we have the customer date and time, and we know the duration, we can just use that.
        // Actually, let's try to parse the end time in the same timezone context.
        if (endTime) {
          // We need to know if the end time lands on the same day as start time in listing time
          // But wait, if we parse it in customerTimeZone, it should works as long as the duration is consistent.
          // This is tricky. Let's assume duration is better if possible.
          const potentialListingDates = getListingDatesForCustomerDate(
            dateString,
            listingTimeZone,
            customerTimeZone
          );
          let lDate = dateString;
          for (const d of potentialListingDates) {
            const startM = momentFromScheduledDateAndUtcTime(
              d,
              camp.dailySchedule.startTime,
              listingTimeZone
            );
            if (startM && startM.clone().tz(customerTimeZone).format('YYYY-MM-DD') === dateString) {
              lDate = d;
              break;
            }
          }
          const endMoment = momentFromScheduledDateAndUtcTime(
            lDate,
            camp.dailySchedule.endTime,
            listingTimeZone
          );
          endDate = endMoment ? endMoment.toDate() : endDate;
        }
      }
    }
    // If no end time, default to same as start (single time slot)
    if (!endDate) {
      endDate = new Date(startDate.getTime());
    }
  }
  // For session/class sessions, calculate from duration
  else if (sessionType.availabilityId) {
    const block = availabilityData.schedulingBlocks[sessionType.availabilityId];

    if (block) {
      if (block.type === 'class' && block.timeSlots && Array.isArray(block.timeSlots) && block.timeSlots.length > 0) {
        const selectedStartLabel = (timeSlot.split(' - ')[0] || '').trim();
        const scheduledForBlock = (availabilityData.scheduledDates || []).filter(
          sd => sd.blockId === sessionType.availabilityId
        );
        let listingDate = dateString;
        if (listingTimeZone && customerTimeZone && listingTimeZone !== customerTimeZone) {
          for (const sd of scheduledForBlock) {
            const customerDay = customerDateFromScheduled(
              sd.date,
              block.timeSlots[0]?.startTime || block.startTime,
              listingTimeZone,
              customerTimeZone
            );
            if (customerDay === dateString) {
              listingDate = sd.date;
              break;
            }
          }
        }
        const matchingSlot = block.timeSlots.find(slot => {
          const startM = momentFromScheduledDateAndUtcTime(
            listingDate,
            slot.startTime,
            listingTimeZone
          );
          if (!startM) return false;
          const displayTz = customerTimeZone || listingTimeZone;
          const customerLabel = formatTimeSlot(startM.clone().tz(displayTz));
          return customerLabel === selectedStartLabel;
        });

        if (matchingSlot?.duration) {
          endDate = calculateBookingEndDate(startDate, matchingSlot.duration);
        }
      } else if (block.duration) {
        endDate = calculateBookingEndDate(startDate, block.duration);
      }
    }
  }

  if (!endDate) {
    // Default: same as start if no duration available
    endDate = new Date(startDate.getTime());
  }

  return {
    bookingStart: startDate,
    bookingEnd: endDate,
  };
};
